import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import StoreIcon from "@material-ui/icons/Store";
import { format, isValid, parseISO } from "date-fns";
import { toast } from "react-toastify";

import ConfirmationModal from "../ConfirmationModal";
import RowActionsMenu from "../RowActionsMenu";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  hero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    color: theme.palette.primary.main,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  helperText: {
    opacity: 0.74,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.6,
    maxWidth: 720,
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${fade(theme.palette.divider, 0.82)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${fade(theme.palette.divider, 0.86)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${fade(theme.palette.divider, 0.86)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    maxHeight: "calc(100vh - 300px)",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "none",
    },
  },
  chip: {
    fontWeight: 600,
  },
  rowDueSoon: {
    backgroundColor: fade("#f59e0b", 0.1),
  },
  rowOverdue: {
    backgroundColor: fade("#ef4444", 0.1),
  },
  rowInactive: {
    opacity: 0.82,
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: "center",
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  dialogPaper: {
    borderRadius: 24,
    overflow: "hidden",
  },
  dialogHeader: {
    padding: theme.spacing(2.5, 3, 1.5),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  dialogTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  dialogTitleText: {
    fontWeight: 600,
  },
  dialogBadge: {
    border: `1px solid ${fade(theme.palette.divider, 0.88)}`,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3, 2.5),
    borderTop: `1px solid ${fade(theme.palette.divider, 0.72)}`,
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 2, 2),
      flexWrap: "wrap",
    },
  },
  formSection: {
    "& + &": {
      borderTop: `1px solid ${fade(theme.palette.divider, 0.72)}`,
      marginTop: theme.spacing(2.5),
      paddingTop: theme.spacing(2.5),
    },
  },
  sectionTitle: {
    fontWeight: 600,
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  field: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 14,
      "& fieldset": {
        borderColor: fade(theme.palette.divider, 0.92),
      },
      "&:hover fieldset": {
        borderColor: fade(theme.palette.primary.main, 0.35),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.12)}`,
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  inlineInsights: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  insightStrip: {
    border: `1px solid ${fade(theme.palette.divider, 0.9)}`,
    borderRadius: 16,
    padding: theme.spacing(1.5, 1.75),
    backgroundColor:
      theme.palette.type === "dark"
        ? fade(theme.palette.background.default, 0.38)
        : fade(theme.palette.action.hover, 0.55),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  insightCopy: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.4),
  },
  insightLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.66,
  },
  insightValue: {
    fontWeight: 600,
    lineHeight: 1.4,
  },
}));

const INITIAL_FORM = {
  id: undefined,
  name: "",
  email: "",
  phone: "",
  planId: "",
  status: true,
  dueDate: "",
  recurrence: "",
  password: "",
  document: "",
  paymentMethod: "",
};

const RECURRENCE_OPTIONS = [
  { value: "", label: "Sem recorrencia" },
  { value: "MENSAL", label: "Mensal" },
  { value: "BIMESTRAL", label: "Bimestral" },
  { value: "TRIMESTRAL", label: "Trimestral" },
  { value: "SEMESTRAL", label: "Semestral" },
  { value: "ANUAL", label: "Anual" },
];

const SUMMARY_CARDS = [
  { key: "total", label: "Total de empresas", accent: "#1d4ed8" },
  { key: "active", label: "Ativas", accent: "#0f766e" },
  { key: "inactive", label: "Inativas", accent: "#6b7280" },
  { key: "dueSoon", label: "Vencendo em breve", accent: "#d97706" },
  { key: "overdue", label: "Em atraso", accent: "#dc2626" },
  { key: "withoutPlan", label: "Sem plano", accent: "#7c3aed" },
];

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  try {
    const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
    return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : "-";
  } catch (error) {
    return "-";
  }
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  try {
    const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
    return isValid(parsed) ? format(parsed, "dd/MM/yy HH:mm") : "-";
  } catch (error) {
    return "-";
  }
};

const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  try {
    const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
    return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : "";
  } catch (error) {
    return "";
  }
};

const getDueMetrics = (value) => {
  if (!value) {
    return {
      label: "Sem vencimento",
      tone: "neutral",
      days: null,
    };
  }

  try {
    const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
    if (!isValid(parsed)) {
      return {
        label: "Sem vencimento",
        tone: "neutral",
        days: null,
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsed.setHours(0, 0, 0, 0);

    const msPerDay = 1000 * 60 * 60 * 24;
    const days = Math.round((parsed.getTime() - today.getTime()) / msPerDay);

    if (days < 0) {
      return {
        label: `Atrasada ha ${Math.abs(days)} dia${Math.abs(days) === 1 ? "" : "s"}`,
        tone: "overdue",
        days,
      };
    }

    if (days === 0) {
      return {
        label: "Vence hoje",
        tone: "overdue",
        days,
      };
    }

    if (days <= 5) {
      return {
        label: `Vence em ${days} dia${days === 1 ? "" : "s"}`,
        tone: "soon",
        days,
      };
    }

    return {
      label: "Adimplente",
      tone: "ok",
      days,
    };
  } catch (error) {
    return {
      label: "Sem vencimento",
      tone: "neutral",
      days: null,
    };
  }
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  if (Number.isNaN(numeric)) {
    return "R$ 0,00";
  }

  return `R$ ${numeric.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const CompaniesManager = () => {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();
  const { list: listPlans } = usePlans();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);
  const [plans, setPlans] = useState([]);
  const [filters, setFilters] = useState({
    searchParam: "",
    status: "",
    planId: "",
    onlyProblems: false,
  });
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [companies, plansList] = await Promise.all([list(), listPlans()]);
      setRecords(Array.isArray(companies) ? companies : []);
      setPlans(Array.isArray(plansList) ? plansList : []);
    } catch (error) {
      toast.error("Nao foi possivel carregar a lista de empresas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(
    () =>
      records.reduce(
        (acc, company) => {
          const due = getDueMetrics(company.dueDate);
          acc.total += 1;
          acc.active += company.status !== false ? 1 : 0;
          acc.inactive += company.status === false ? 1 : 0;
          acc.dueSoon += due.tone === "soon" ? 1 : 0;
          acc.overdue += due.tone === "overdue" ? 1 : 0;
          acc.withoutPlan += company.planId ? 0 : 1;
          return acc;
        },
        {
          total: 0,
          active: 0,
          inactive: 0,
          dueSoon: 0,
          overdue: 0,
          withoutPlan: 0,
        }
      ),
    [records]
  );

  const filteredRecords = useMemo(
    () =>
      records.filter((company) => {
        const searchText = [
          company.name,
          company.email,
          company.phone,
          company.document,
          company.plan?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const matchesSearch = searchText.includes(
          filters.searchParam.trim().toLowerCase()
        );

        const matchesStatus =
          !filters.status ||
          (filters.status === "active" && company.status !== false) ||
          (filters.status === "inactive" && company.status === false);

        const matchesPlan =
          !filters.planId || String(company.planId || "") === String(filters.planId);

        const due = getDueMetrics(company.dueDate);
        const matchesProblems =
          !filters.onlyProblems ||
          due.tone === "soon" ||
          due.tone === "overdue" ||
          company.status === false;

        return matchesSearch && matchesStatus && matchesPlan && matchesProblems;
      }),
    [filters, records]
  );

  const selectedPlan = useMemo(
    () =>
      plans.find((plan) => String(plan.id) === String(formData.planId)) || null,
    [formData.planId, plans]
  );

  const hasActiveFilters = Boolean(
    filters.searchParam.trim() ||
      filters.status ||
      filters.planId ||
      filters.onlyProblems
  );

  const resetForm = () => {
    setFormData(INITIAL_FORM);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleOpenEditModal = (company) => {
    setFormData({
      id: company.id,
      name: company.name || "",
      email: company.email || "",
      phone: company.phone || "",
      planId: company.planId || "",
      status: company.status !== false,
      dueDate: toInputDate(company.dueDate),
      recurrence: company.recurrence || "",
      password: "",
      document: company.document || "",
      paymentMethod: company.paymentMethod || "",
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);
    resetForm();
  };

  const handleFilterChange = (field) => (event) => {
    const value =
      field === "onlyProblems" ? event.target.checked : event.target.value;

    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleFormFieldChange = (field) => (event) => {
    setFormData((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      searchParam: "",
      status: "",
      planId: "",
      onlyProblems: false,
    });
  };

  const handleIncrementDueDate = () => {
    if (!formData.dueDate || !formData.recurrence) {
      return;
    }

    const currentDate = new Date(`${formData.dueDate}T00:00:00`);
    if (!isValid(currentDate)) {
      return;
    }

    const updatedDate = new Date(currentDate);

    switch (formData.recurrence) {
      case "MENSAL":
        updatedDate.setMonth(updatedDate.getMonth() + 1);
        break;
      case "BIMESTRAL":
        updatedDate.setMonth(updatedDate.getMonth() + 2);
        break;
      case "TRIMESTRAL":
        updatedDate.setMonth(updatedDate.getMonth() + 3);
        break;
      case "SEMESTRAL":
        updatedDate.setMonth(updatedDate.getMonth() + 6);
        break;
      case "ANUAL":
        updatedDate.setFullYear(updatedDate.getFullYear() + 1);
        break;
      default:
        return;
    }

    setFormData((current) => ({
      ...current,
      dueDate: format(updatedDate, "yyyy-MM-dd"),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        planId: formData.planId ? Number(formData.planId) : undefined,
        dueDate: formData.dueDate || null,
      };

      if (!payload.id && !payload.password) {
        toast.error("Informe uma senha inicial para a empresa.");
        setSaving(false);
        return;
      }

      if (payload.id && !payload.password) {
        delete payload.password;
      }

      if (payload.id) {
        await update(payload);
        toast.success("Empresa atualizada com sucesso.");
      } else {
        await save(payload);
        toast.success("Empresa cadastrada com sucesso.");
      }

      await loadData();
      handleCloseModal();
    } catch (error) {
      toast.error(
        "Nao foi possivel salvar a empresa. Revise nome, plano, e-mail e senha inicial."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!recordToDelete) {
      return;
    }

    setSaving(true);
    try {
      await remove(recordToDelete.id);
      toast.success("Empresa excluida com sucesso.");
      await loadData();

      if (formData.id === recordToDelete.id) {
        handleCloseModal();
      }
    } catch (error) {
      toast.error("Nao foi possivel excluir a empresa.");
    } finally {
      setSaving(false);
      setRecordToDelete(null);
    }
  };

  const renderStatusChip = (company) => (
    <Chip
      size="small"
      label={company.status === false ? "Inativa" : "Ativa"}
      className={classes.chip}
      style={
        company.status === false
          ? { backgroundColor: "#e5e7eb", color: "#374151" }
          : { backgroundColor: "#ccfbf1", color: "#115e59" }
      }
    />
  );

  const renderDueChip = (company) => {
    const due = getDueMetrics(company.dueDate);

    let styles = { backgroundColor: "#e5e7eb", color: "#374151" };
    if (due.tone === "soon") {
      styles = { backgroundColor: "#fef3c7", color: "#92400e" };
    }
    if (due.tone === "overdue") {
      styles = { backgroundColor: "#fee2e2", color: "#991b1b" };
    }
    if (due.tone === "ok") {
      styles = { backgroundColor: "#dcfce7", color: "#166534" };
    }

    return (
      <Chip
        size="small"
        icon={<EventAvailableIcon />}
        label={due.label}
        className={classes.chip}
        style={styles}
      />
    );
  };

  const getRowClassName = (company) => {
    const due = getDueMetrics(company.dueDate);

    if (due.tone === "overdue") {
      return classes.rowOverdue;
    }

    if (due.tone === "soon") {
      return classes.rowDueSoon;
    }

    if (company.status === false) {
      return classes.rowInactive;
    }

    return "";
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title="Excluir empresa"
        open={Boolean(recordToDelete)}
        onClose={() => setRecordToDelete(null)}
        onConfirm={handleDelete}
      >
        Deseja realmente excluir a empresa
        {recordToDelete ? ` "${recordToDelete.name}"` : ""}?
      </ConfirmationModal>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle disableTypography className={classes.dialogHeader}>
            <Typography className={classes.eyebrow}>
              Cadastro empresarial
            </Typography>
            <Box className={classes.dialogTop}>
              <Box>
                <Typography variant="h6" className={classes.dialogTitleText}>
                  {formData.id ? "Editar empresa" : "Nova empresa"}
                </Typography>
                <Typography variant="body2" className={classes.helperText}>
                  Revise identidade, acesso inicial, plano e vigencia em um
                  formulario mais direto, com menos blocos visuais.
                </Typography>
              </Box>
              <Chip
                size="small"
                label={formData.id ? `ID #${formData.id}` : "Novo cadastro"}
                className={classes.dialogBadge}
              />
            </Box>
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Box className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                Dados principais
              </Typography>
              <Typography variant="body2" className={classes.sectionSubtitle}>
                Identificacao da empresa, acesso inicial e vinculo com o plano
                comercial.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    label="Empresa"
                    value={formData.name}
                    onChange={handleFormFieldChange("name")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    label="E-mail principal"
                    type="email"
                    value={formData.email}
                    onChange={handleFormFieldChange("email")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Telefone"
                    value={formData.phone}
                    onChange={handleFormFieldChange("phone")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={classes.field}
                    select
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    label="Plano comercial"
                    value={String(formData.planId)}
                    onChange={handleFormFieldChange("planId")}
                  >
                    <MenuItem value="">Selecione um plano</MenuItem>
                    {plans.map((plan) => (
                      <MenuItem key={plan.id} value={String(plan.id)}>
                        {plan.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={classes.field}
                    select
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Status comercial"
                    value={String(formData.status)}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        status: event.target.value === "true",
                      }))
                    }
                  >
                    <MenuItem value="true">Ativa</MenuItem>
                    <MenuItem value="false">Inativa</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label={formData.id ? "Nova senha (opcional)" : "Senha inicial"}
                    type="password"
                    value={formData.password}
                    onChange={handleFormFieldChange("password")}
                    helperText={
                      formData.id
                        ? "Preencha apenas se quiser trocar a senha atual."
                        : "Obrigatoria para criar o usuario administrador da empresa."
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box className={classes.formSection}>
              <Typography className={classes.sectionTitle}>
                Contrato e cobranca
              </Typography>
              <Typography variant="body2" className={classes.sectionSubtitle}>
                Dados financeiros e regras de renovacao usados para acompanhar a
                vigencia comercial da conta.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Documento"
                    value={formData.document}
                    onChange={handleFormFieldChange("document")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Forma de pagamento"
                    value={formData.paymentMethod}
                    onChange={handleFormFieldChange("paymentMethod")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    className={classes.field}
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="date"
                    label="Proximo vencimento"
                    value={formData.dueDate}
                    onChange={handleFormFieldChange("dueDate")}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    className={classes.field}
                    select
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Recorrencia"
                    value={formData.recurrence}
                    onChange={handleFormFieldChange("recurrence")}
                  >
                    {RECURRENCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value || "empty"} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Box className={classes.inlineInsights}>
                <Box className={classes.insightStrip}>
                  <Box className={classes.insightCopy}>
                    <Typography className={classes.insightLabel}>
                      Faixa comercial atual
                    </Typography>
                    <Typography variant="body2" className={classes.insightValue}>
                      {selectedPlan
                        ? `${selectedPlan.name} - ${formatCurrency(selectedPlan.amount)}`
                        : "Selecione um plano para visualizar o valor"}
                    </Typography>
                  </Box>
                  <StoreIcon color="action" />
                </Box>

                <Box className={classes.insightStrip}>
                  <Box className={classes.insightCopy}>
                    <Typography className={classes.insightLabel}>
                      Ajuste rapido de vigencia
                    </Typography>
                    <Typography variant="body2" className={classes.insightValue}>
                      Avance o vencimento automaticamente conforme a recorrencia
                      selecionada.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleIncrementDueDate}
                    disabled={!formData.dueDate || !formData.recurrence}
                  >
                    Prorrogar vencimento
                  </Button>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            {formData.id ? (
              <Button
                color="secondary"
                startIcon={<DeleteOutlineIcon />}
                onClick={() =>
                  setRecordToDelete({
                    id: formData.id,
                    name: formData.name,
                  })
                }
                disabled={saving}
              >
                Excluir
              </Button>
            ) : (
              <Box flex={1} />
            )}
            <Button onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Box className={classes.hero}>
        <Box>
          <Typography className={classes.eyebrow}>Settings</Typography>
          <Typography variant="h6" className={classes.heroTitle}>
            Gestao de empresas
          </Typography>
        </Box>
        <Box className={classes.actionsBar}>
          <Button
            variant="outlined"
            startIcon={<AutorenewIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
          >
            Nova empresa
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {SUMMARY_CARDS.map((card) => (
          <Grid item xs={6} sm={4} md={2} key={card.key}>
            <Paper
              className={classes.summaryCard}
              elevation={0}
              style={{
                background: `linear-gradient(180deg, ${fade(
                  card.accent,
                  0.14
                )} 0%, ${fade(card.accent, 0.05)} 100%)`,
              }}
            >
              <Typography className={classes.summaryLabel}>{card.label}</Typography>
              <Typography className={classes.summaryValue}>
                {summary[card.key] || 0}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper className={classes.filtersPaper} elevation={0}>
        <Box className={classes.filtersHeader}>
          <Typography variant="body1" className={classes.filtersTitle}>
            Filtros
          </Typography>
          <Box className={classes.actionsBar}>
            {hasActiveFilters ? (
              <Chip
                size="small"
                label="Filtros ativos"
                style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
              />
            ) : null}
            <Button onClick={clearFilters} disabled={!hasActiveFilters}>
              Limpar
            </Button>
          </Box>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              className={classes.field}
              fullWidth
              variant="outlined"
              size="small"
              label="Busca"
              placeholder="Empresa, e-mail, telefone, documento ou plano"
              value={filters.searchParam}
              onChange={handleFilterChange("searchParam")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              className={classes.field}
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Status"
              value={filters.status}
              onChange={handleFilterChange("status")}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="active">Ativas</MenuItem>
              <MenuItem value="inactive">Inativas</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              className={classes.field}
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Plano"
              value={filters.planId}
              onChange={handleFilterChange("planId")}
            >
              <MenuItem value="">Todos os planos</MenuItem>
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={String(plan.id)}>
                  {plan.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <TextField
              className={classes.field}
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Foco"
              value={String(filters.onlyProblems)}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  onlyProblems: event.target.value === "true",
                }))
              }
            >
              <MenuItem value="false">Tudo</MenuItem>
              <MenuItem value="true">Criticas</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.tablePaper} elevation={0}>
        <Box className={classes.tableHeader}>
          <Typography variant="body1" className={classes.tableHeaderTitle}>
            Empresas
          </Typography>
          <Box className={classes.actionsBar}>
            <Chip
              size="small"
              label={`${filteredRecords.length} resultado${
                filteredRecords.length === 1 ? "" : "s"
              }`}
              style={{ backgroundColor: "#eef2ff", color: "#4338ca" }}
            />
            <Chip
              size="small"
              label={loading ? "Atualizando" : "Sincronizado"}
              style={
                loading
                  ? { backgroundColor: "#fef3c7", color: "#92400e" }
                  : { backgroundColor: "#e2e8f0", color: "#475569" }
              }
            />
          </Box>
        </Box>
        {loading && <LinearProgress />}
        <TableContainer className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Recorrencia</TableCell>
                <TableCell>Criada em</TableCell>
                <TableCell>Ultimo acesso</TableCell>
                <TableCell align="right" className={classes.actionsCell}>Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                filteredRecords.map((company) => (
                  <TableRow key={company.id} className={getRowClassName(company)}>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>
                        {company.name || "-"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        ID #{company.id}
                        {company.document ? ` - ${company.document}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {company.plan?.name || "Sem plano"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {company.plan ? formatCurrency(company.plan.amount) : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{company.email || "-"}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {company.phone || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderStatusChip(company)}</TableCell>
                    <TableCell>
                      <Typography className={classes.nowrap}>
                        {formatDate(company.dueDate)}
                      </Typography>
                      <Box mt={0.5}>{renderDueChip(company)}</Box>
                    </TableCell>
                    <TableCell>{company.recurrence || "-"}</TableCell>
                    <TableCell className={classes.nowrap}>
                      {formatDate(company.createdAt)}
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      {formatDateTime(company.lastLogin)}
                    </TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      <RowActionsMenu
                        buttonTitle="Acoes da empresa"
                        actions={[
                          {
                            key: "edit",
                            label: "Editar empresa",
                            icon: EditIcon,
                            onClick: () => handleOpenEditModal(company),
                          },
                          {
                            key: "delete",
                            label: "Excluir empresa",
                            icon: DeleteOutlineIcon,
                            onClick: () => setRecordToDelete(company),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && filteredRecords.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="h6">
              {hasActiveFilters
                ? "Nenhuma empresa encontrada para os filtros aplicados"
                : "Nenhuma empresa cadastrada"}
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              Cadastre uma nova empresa ou ajuste os filtros para continuar.
            </Typography>
          </Box>
        ) : null}
      </Paper>
    </div>
  );
};

export default CompaniesManager;
